import { useEffect, useState } from "react";
import { produce } from "immer";
import useSWR, { mutate } from "swr";

import { ToastId } from "@catchoftheday/cg-components";
import { legacyEventBus, useEventBus } from "@catchoftheday/state";
import {
  createToast,
  ToastContentProps,
  updateToast,
} from "@catchoftheday/toast";

import { fetcher } from "../../api/index";
import { HEADER_META_DATA_API } from "../../consts/index";
import { HeaderConfiguration, HeaderMetaData } from "../../types/index";
import {
  localStorage,
  updateWishlistStateOptimistically,
} from "../../utilities/index";

export type ToastEvent = {
  toastContent: ToastContentProps;
  toastId: ToastId;
  autoClose?: number | false;
};

const createToastHandler = (toastEvent: ToastEvent) => {
  createToast(toastEvent);
};
const updateToastHandler = (toastEvent: ToastEvent) => {
  updateToast(toastEvent);
};

export const useHeaderMetaData = (
  headerConfiguration?: HeaderConfiguration
) => {
  const [firstRequestComplete, setFirstRequestComplete] = useState(false);

  const initialHeaderMetaData = headerConfiguration
    ? {
        displayName: "",
        loggedIn: headerConfiguration.primaryHeader.myAccount.loggedIn,
        flybuysLinked: headerConfiguration.primaryHeader.flybuys.linked,
        cartItemCount: Number(headerConfiguration.primaryHeader.cart.itemCount),
        wishlistItemCount: Number(
          headerConfiguration.primaryHeader.wishlist.itemCount
        ),
        messageCount: headerConfiguration.primaryHeader.myAccount.messageCount,
        megaMenuEnabled: headerConfiguration.megaMenu?.enabled,
        navData: headerConfiguration.megaMenu?.navData?.shops,
        essentialsMenu:
          headerConfiguration.megaMenu?.navData?.catchEssentialsMenu,
        accountMenu: headerConfiguration.megaMenu?.navData?.accountMenu,
        couponNotificationEnabled: false,
      }
    : undefined;

  useEventBus(
    { type: "@cart-app/ADD_TO_CART" },
    () => {
      mutate(
        HEADER_META_DATA_API,
        async (currentData?: { data: HeaderMetaData }) =>
          produce(currentData, (newState) => {
            if (newState?.data) {
              newState.data.cartItemCount =
                Number(newState.data.cartItemCount) + 1;
            }
          })
      );
    },
    []
  );

  useEventBus(
    { type: "@cart-app/REMOVE_ITEM_FROM_CART" },
    () => {
      mutate(
        HEADER_META_DATA_API,
        async (currentData?: { data: HeaderMetaData }) =>
          produce(currentData, (newState) => {
            if (newState?.data) {
              newState.data.cartItemCount =
                Number(newState.data.cartItemCount) - 1;
            }
          })
      );
    },
    []
  );

  useEventBus(
    { type: "@cart-app/ADD_TO_WISHLIST" },
    () => {
      mutate(
        HEADER_META_DATA_API,
        async (currentData?: { data: HeaderMetaData }) =>
          produce(currentData, (newState) => {
            if (newState?.data) {
              newState.data.wishlistItemCount =
                Number(newState.data.wishlistItemCount) + 1;
            }
          })
      );
    },
    []
  );

  useEventBus<{ cartItemCount: number }>(
    { type: "@cart-app/UPDATE_PRODUCT_QUANTITY" },
    ({ payload }) => {
      mutate(
        HEADER_META_DATA_API,
        async (currentData?: { data: HeaderMetaData }) =>
          produce(currentData, (newState) => {
            if (newState?.data && payload?.cartItemCount) {
              newState.data.cartItemCount = Number(payload?.cartItemCount);
            }
          })
      );
    },
    []
  );

  useEffect(() => {
    legacyEventBus?.on("GET_CART", ({ data: eventData }) => {
      mutate(
        HEADER_META_DATA_API,
        async (currentData?: { data: HeaderMetaData }) =>
          produce(currentData, (newState) => {
            if (newState?.data) {
              newState.data.cartItemCount = eventData.quantity;
            }
          })
      );
    });

    legacyEventBus?.on("UPDATE_WISHLIST", () => {
      updateWishlistStateOptimistically();
    });
  }, []);

  useEffect(() => {
    legacyEventBus?.on("CREATE_TOAST", createToastHandler);
    return () => {
      legacyEventBus?.detach("CREATE_TOAST", createToastHandler);
    };
  }, []);

  useEffect(() => {
    legacyEventBus?.on("UPDATE_TOAST", updateToastHandler);
    return () => {
      legacyEventBus?.detach("UPDATE_TOAST", updateToastHandler);
    };
  }, []);

  const { data: response, isValidating } = useSWR<{
    data: HeaderMetaData;
  }>(HEADER_META_DATA_API, fetcher, {
    onError: () => {
      setFirstRequestComplete(true);
    },
    onErrorRetry: () => {
      setFirstRequestComplete(true);
    },
    onSuccess: (data, key) => {
      setFirstRequestComplete(true);
      localStorage.setItem(
        key,
        JSON.stringify({
          data: data?.data,
        })
      );
    },
  });

  /** Use localStorage to cache user information */
  const metaData: HeaderMetaData | undefined =
    response?.data ||
    initialHeaderMetaData ||
    JSON.parse(localStorage.getItem(HEADER_META_DATA_API) || "{}")?.data ||
    undefined;

  return {
    metaData,
    initialHeaderMetaData,
    isLoading: (!metaData && isValidating) || !firstRequestComplete,
  };
};
